import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { TOOLTIP } from '../../../../constants/tooltips'
import { FormFieldsEdit } from '../../../../types/form-types'
import { useLookup } from '../../../hooks/use-api'
import { AddField, RemoveField } from '../../../hooks/use-edit-form'
import useUser from '../../../hooks/use-user'
import { ActivityType } from '../../../services/constants'
import { isDriller } from '../../../services/user-utils'
import LookupField from '../../wells/lookup-field'
import { WellDetailsComponentBase } from './well-details-types'
import { CheckableField, CheckableFields, FormField, Heading, TextInput, TypeaheadOption } from '@msaf/core-react'

interface WellDetailsConsentEditProps extends WellDetailsComponentBase {
  addField: AddField<FormFieldsEdit>
  removeField: RemoveField<FormFieldsEdit>
  setIsWellGrantedAsLegacy?: Dispatch<SetStateAction<boolean>>
}

export default function WellDetailsConsentEdit({
  isLoading,
  isSetupRecord,
  setValue,
  validationErrors,
  fields,
  setIsWellGrantedAsLegacy,
}: WellDetailsConsentEditProps) {
  const [activityTypes, setActivityTypes] = useState<{ id: string; name: string }[]>()
  const [isConsented, setIsConsented] = useState(false)
  const { data } = useLookup('activityType')

  const handleWellGrantedAs = ({ label }: TypeaheadOption) => {
    if (setIsWellGrantedAsLegacy) {
      setIsWellGrantedAsLegacy(label === ActivityType.LEGACY)
    }
  }

  useEffect(() => {
    if (Array.isArray(data)) {
      setActivityTypes(data)

      const activityTypeName = data.find((item) => item.id === fields.activityType)?.name

      if (activityTypeName === ActivityType.LEGACY && setIsWellGrantedAsLegacy) {
        setIsWellGrantedAsLegacy(true)
      }

      setIsConsented(activityTypeName === ActivityType.CONSENTED)
    }
  }, [data, fields.activityType, setIsWellGrantedAsLegacy])

  return (
    <>
      <Heading isSkeleton={isLoading} level={3}>
        Well installation consent
      </Heading>

      {isSetupRecord && (
        <WellDetailsConsentSetupRecord
          isLoading={isLoading}
          isSetupRecord={isSetupRecord}
          validationErrors={validationErrors}
          fields={fields}
          setValue={setValue}
          setIsWellGrantedAsLegacy={setIsWellGrantedAsLegacy}
          activityTypes={activityTypes}
          isConsented={isConsented}
        />
      )}

      {!isSetupRecord && (
        <>
          <FormField
            isSkeleton={isLoading}
            label='Activity type'
            labelId='activityType'
            validationMessages={validationErrors?.activityType}
          >
            <LookupField
              isSkeleton={isLoading}
              labelledBy='activityType'
              fieldId='activityType'
              value={`${fields.activityType ?? ''}`}
              setValue={({ value }) => setValue('activityType', value)}
              handleChangeCallback={handleWellGrantedAs}
              isInvalid={!!validationErrors?.activityType}
            />
          </FormField>
          {isConsented && (
            <FormField
              toolTipMessage={TOOLTIP.boreConsentNo}
              isSkeleton={isLoading}
              label='Well consent/authorisation number'
              htmlFor='wellConsentNo'
              validationMessages={validationErrors?.boreConsentNo}
            >
              <TextInput
                isSkeleton={isLoading}
                id='wellConsentNo'
                value={`${fields.boreConsentNo ?? ''}`}
                setValue={(value) => setValue('boreConsentNo', value)}
                isInvalid={!!validationErrors?.boreConsentNo}
              />
            </FormField>
          )}
        </>
      )}
    </>
  )
}

type WellDetailsConsentSetupRecordType = Omit<WellDetailsConsentEditProps, 'addField' | 'removeField'>
interface WellDetailsConsentEditSetupRecordProps extends WellDetailsConsentSetupRecordType {
  activityTypes?: { id: string; name: string }[]
  isConsented: boolean
}

function WellDetailsConsentSetupRecord({
  isLoading,
  validationErrors,
  fields,
  setIsWellGrantedAsLegacy,
  setValue,
  activityTypes,
  isConsented,
}: WellDetailsConsentEditSetupRecordProps) {
  const { user } = useUser()

  const handleWellGrantedAsChange = (activityType: ActivityType) => {
    if (setIsWellGrantedAsLegacy) {
      setIsWellGrantedAsLegacy(activityType === ActivityType.LEGACY)
    }

    const activityTypeId = activityTypes?.find((item) => item.name === activityType)?.id
    setValue('activityType', activityTypeId ?? '')
  }
  return (
    <>
      <FormField
        isSkeleton={isLoading}
        label='Well granted as'
        labelId='activityTypeLabelId'
        validationMessages={validationErrors?.activityType}
      >
        <CheckableFields ariaLabelledBy='wellGrantedAsId' isHorizontal>
          {!isDriller(user) && (
            <CheckableField
              isSkeleton={isLoading}
              name='wellGrantedAs'
              id='wellGrantedAsConsented'
              type='radio'
              label='Consented activity'
              // Default to checked to Consented activity apart from driller who does not have consented activity as an option
              checked={
                activityTypes?.find((item: any) => item.id === Number(fields.activityType))?.name ===
                ActivityType.CONSENTED
              }
              onChange={() => handleWellGrantedAsChange(ActivityType.CONSENTED)}
              describedBy='wellGrantedAsId'
            />
          )}
          <CheckableField
            isSkeleton={isLoading}
            name='wellGrantedAs'
            id='wellGrantedAsPermitted'
            type='radio'
            label='Permitted activity'
            checked={
              activityTypes?.find((item: any) => item.id === Number(fields.activityType))?.name ===
              ActivityType.PERMITTED
            }
            onChange={() => handleWellGrantedAsChange(ActivityType.PERMITTED)}
            describedBy='wellGrantedAsId'
          />
          <CheckableField
            isSkeleton={isLoading}
            name='wellGrantedAs'
            id='wellGrantedAsLegacy'
            type='radio'
            label='Legacy Activity'
            checked={
              activityTypes?.find((item: any) => item.id === Number(fields.activityType))?.name === ActivityType.LEGACY
            }
            onChange={() => handleWellGrantedAsChange(ActivityType.LEGACY)}
            describedBy='wellGrantedAsId'
          />
        </CheckableFields>
      </FormField>
      {isConsented && (
        <FormField
          isSkeleton={isLoading}
          label='Well consent/authorisation number'
          htmlFor='wellConsentNo'
          validationMessages={validationErrors?.boreConsentNo}
        >
          <TextInput
            isSkeleton={isLoading}
            id='wellConsentNo'
            value={`${fields.boreConsentNo ?? ''}`}
            setValue={(value) => setValue('boreConsentNo', value)}
            isInvalid={!!validationErrors?.boreConsentNo}
          />
        </FormField>
      )}
    </>
  )
}
